import {
  BODY_FONT,
  HEADER_FONT_BOLD_11,
  hideColumnsAfter,
  hideRowsThrough,
  NA_FILL,
  SUBTITLE_FONT,
  THIN_BLACK,
  TITLE_FONT,
  VALUE_FILL,
} from "./detailSheetCommon";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF33CCFF" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { theme: 0, tint: -0.249977111117893 },
};

const THIN = { style: "thin", color: { argb: "FF000000" } };
const MEDIUM = { style: "medium", color: { argb: "FF000000" } };
const UNUSED_FILL = TABLE_HEADER_FILL;
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

const COL_QUESTION = 4;
const COL_P_START = 5;
const COL_P_END = 13;
const COL_FREQ_START = 14;
const COL_FREQ_END = 22;
const COL_GOV_LABEL_END = 11;
const COL_GOV_ANSWER = 12;
const PRINCIPLE_COUNT = 9;

const SECTION_ROW = 8;
const HEADER_ROW = 9;

const LABELS = {
  sectionTitle: "SECTION B: MANAGEMENT AND PROCESS DISCLOSURES",
  disclosureQuestions: "Disclosure Questions",
  policyBand: "Policy and management processes",
  q1a:
    "1. a. Whether your entity\u2019s policy/policies cover each principle and its core elements of the NGRBCs. (Yes/No/NA)",
  q1b: "b. Has the policy been approved by the Board? (Yes/No/NA)",
  q1c: "c. Web Link of the Policies, if available",
  q2: "2. Whether the entity has translated the policy into procedures. (Yes / No/ NA)",
  q3: "3. Do the enlisted policies extend to your value chain partners? (Yes/No/NA)",
  q4:
    "4. Name of the national and international codes/certifications/labels/ standards (e.g. Forest Stewardship Council, Fairtrade, Rainforest Alliance, Trustee) standards (e.g. SA 8000, OHSAS, ISO, BIS) adopted by your entity and mapped to each principle.",
  q5: "5. Specific commitments, goals and targets set by the entity with defined timelines, if any.",
  q6:
    "6. Performance of the entity against the specific commitments, goals and targets along-with reasons in case the same are not met.",
  governanceBand: "Governance, leadership and oversight",
  q7:
    "7. Statement by director responsible for the business responsibility report, highlighting ESG related challenges, targets and achievements (listed entity has flexibility regarding the placement of this disclosure)",
  q8:
    "8. Details of the highest authority responsible for implementation and oversight of the Business Responsibility policy (ies).",
  q9:
    "9. Does the entity have a specified Committee of the Board/ Director responsible for decision making on sustainability related issues? (Yes / No/ NA).",
  q9Details: "If yes, provide details.",
  q10: "10. Details of Review of NGRBCs by the Company",
  subjectForReview: "Subject for Review",
  reviewHeader:
    "Indicate whether review was undertaken by Director/Committee of the Board/Any other Committee",
  freqHeader:
    "Frequency\n(Annually / Half yearly /Quarterly/ Any other-please specify)",
  reviewPerformance: "Performance against above policies and follow up action",
  reviewPerformanceDesc:
    "Description of other committee for performance against above policies and follow up action",
  reviewCompliance:
    "Compliance with statutory requirements of relevance to the principles and rectification of any non-compliances",
  reviewComplianceDesc:
    "Description of other committee for compliance with statutory requirements of relevance to the principles and rectification",
  q11:
    "11. Has the entity carried out independent assessment/ evaluation of the working of its policies by an external agency? (Yes/No).",
  q11Agency: "If Yes, Provide name of the agency",
  q12:
    "12. If answer to question (1) above is “No” i.e. not all Principles are covered by a policy, reasons to be stated:",
  questionsHeader: "Questions",
  reasonNonMaterial:
    "The entity does not consider the Principles material to its business (Yes/No)",
  reasonNotReady:
    "The entity is not at a stage where it is in a position to formulate and implement the policies on specified principles (Yes/No)",
  reasonResources:
    "The entity does not have the financial or/human and technical resources available for the task (Yes/No)",
  reasonPlannedNextYear: "It is planned to be done in the next financial year (Yes/No)",
  reasonOther: "Any other reason (please specify)",
  notes: "Notes",
};

const LINE_HEIGHT = 15;
const DEFAULT_MIN_ROW_HEIGHT = 18;

function estimateRowHeight(text, colWidth, minHeight = DEFAULT_MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.85));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  const height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    DEFAULT_MIN_ROW_HEIGHT
  );
  ws.getRow(row).height = height;
}

function applySectionTitleStyle(ws, row) {
  for (let col = COL_QUESTION; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = HEADER_FILL;
    cell.border = {
      top: MEDIUM,
      bottom: MEDIUM,
      left: col === COL_QUESTION ? MEDIUM : undefined,
      right: col === COL_FREQ_END ? MEDIUM : undefined,
    };
  }
}

function applyTableHeaderStyle(ws, row) {
  for (let col = COL_QUESTION; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = TABLE_HEADER_FILL;
    cell.border = {
      left: col === COL_QUESTION ? MEDIUM : THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
}

function applyUnusedRightArea(ws, row, { bottom, top } = {}) {
  for (let col = COL_FREQ_START; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = UNUSED_FILL;
    const border = {};
    if (col === COL_FREQ_END) border.right = MEDIUM;
    if (bottom) border.bottom = bottom;
    if (top) border.top = top;
    if (Object.keys(border).length) cell.border = border;
  }
}

function applySubBandStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = { top: THIN, left: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyGovernanceBandStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = {
      top: THIN,
      bottom: THIN,
      right: col === COL_P_END ? THIN : undefined,
    };
  }
  applyUnusedRightArea(ws, row);
}

function applyYesNoPrincipleStyle(ws, row, options = {}) {
  const labelBottom = "labelBottom" in options ? options.labelBottom : THIN;
  ws.getCell(row, COL_QUESTION).border = {
    top: THIN,
    left: MEDIUM,
    ...(labelBottom ? { bottom: labelBottom } : {}),
  };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = VALUE_FILL;
    cell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  }
  for (let col = COL_FREQ_START; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = NA_FILL;
    cell.border = {
      top: THIN,
      left: col === COL_FREQ_START ? undefined : THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
}

function applyTextPrincipleStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = { left: THIN, bottom: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyWebLinkRowStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = VALUE_FILL;
    const noBorderCols = [COL_P_START, COL_P_START + 3];
    cell.border = noBorderCols.includes(col)
      ? {}
      : { left: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyGovernanceRowStyle(ws, row, { greyAnswerSpacer = false, looseTop = false } = {}) {
  if (looseTop) {
    ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM };
    for (let col = COL_P_START; col <= COL_GOV_LABEL_END - 1; col += 1) {
      ws.getCell(row, col).border = { top: THIN };
    }
    ws.getCell(row, COL_GOV_LABEL_END).border = { top: THIN, right: THIN };
  } else {
    ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
    for (let col = COL_P_START; col <= COL_GOV_LABEL_END; col += 1) {
      ws.getCell(row, col).border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
    }
  }

  const answerCell = ws.getCell(row, COL_GOV_ANSWER);
  answerCell.fill = VALUE_FILL;
  answerCell.border = looseTop
    ? { top: THIN, right: THIN }
    : { top: THIN, left: THIN, bottom: THIN, right: THIN };

  const spacerCell = ws.getCell(row, COL_P_END);
  spacerCell.fill = greyAnswerSpacer ? NA_FILL : UNUSED_FILL;
  spacerCell.border = greyAnswerSpacer
    ? { left: THIN, right: THIN }
    : { left: THIN, bottom: THIN, right: THIN };

  applyUnusedRightArea(ws, row);
}

function applyReviewTitleStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = { bottom: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyReviewHeaderStyle(ws, row, pRow) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, right: THIN };
  for (let col = COL_P_START; col <= COL_FREQ_END; col += 1) {
    ws.getCell(row, col).border = {
      top: THIN,
      left: THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
  ws.getCell(pRow, COL_QUESTION).border = { left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_FREQ_END; col += 1) {
    ws.getCell(pRow, col).border = {
      top: THIN,
      left: THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
}

function applyReviewDataStyle(ws, row, options = {}) {
  const labelBottom = "labelBottom" in options ? options.labelBottom : THIN;
  const freqFill = options.freqFill ?? VALUE_FILL;
  ws.getCell(row, COL_QUESTION).border = {
    top: THIN,
    left: MEDIUM,
    ...(labelBottom ? { bottom: labelBottom } : {}),
  };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = VALUE_FILL;
    cell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  }
  for (let col = COL_FREQ_START; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = freqFill;
    cell.border = {
      top: THIN,
      left: col === COL_FREQ_START ? undefined : THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
}

function applyReviewDescStyle(ws, row) {
  applyReviewDataStyle(ws, row, { freqFill: NA_FILL });
}

function applyReviewDescGreyStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = NA_FILL;
    cell.border = {
      top: THIN,
      left: THIN,
      bottom: THIN,
      right: col === COL_FREQ_END ? MEDIUM : THIN,
    };
  }
}

function applyQ11HeaderStyle(ws, row, nextRow) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, right: THIN };
  ws.getCell(nextRow, COL_QUESTION).border = { left: MEDIUM, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = { left: THIN, bottom: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
  applyUnusedRightArea(ws, nextRow);
}

function applyQ11DataStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { left: MEDIUM, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = VALUE_FILL;
    cell.border = { top: THIN, left: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyQ11AgencyStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  const answerCell = ws.getCell(row, COL_P_START);
  answerCell.fill = VALUE_FILL;
  answerCell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  for (let col = COL_P_START + 1; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = NA_FILL;
    cell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row, { bottom: THIN });
}

function applyQ12TitleStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { left: MEDIUM, bottom: THIN };
  for (let col = COL_P_START; col <= COL_P_END - 1; col += 1) {
    ws.getCell(row, col).border = { bottom: THIN };
  }
  ws.getCell(row, COL_P_END).border = { bottom: THIN, right: THIN };
  applyUnusedRightArea(ws, row);
}

function applyQ12HeaderStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN, right: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    ws.getCell(row, col).border = { top: THIN, left: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyReasonRowStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: THIN };
  for (let col = COL_P_START; col <= COL_P_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = NA_FILL;
    cell.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  }
  applyUnusedRightArea(ws, row);
}

function applyNotesRowStyle(ws, row) {
  ws.getCell(row, COL_QUESTION).border = { top: THIN, left: MEDIUM, bottom: MEDIUM };
  ws.getCell(row, COL_P_START).border = { top: THIN, left: THIN, bottom: MEDIUM, right: THIN };
  for (let col = COL_P_START + 1; col <= COL_FREQ_END; col += 1) {
    const cell = ws.getCell(row, col);
    cell.fill = UNUSED_FILL;
    cell.border = {
      bottom: MEDIUM,
      right: col === COL_FREQ_END ? MEDIUM : undefined,
    };
  }
}

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || String(value), hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.font || (options.bold ? HEADER_FONT_BOLD_11 : BODY_FONT);
  }
  if (options.bordered) borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) cell.fill = options.fill;
}

function toYesNoNa(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  if (value === null || value === undefined || value === "") return "NA";
  return String(value);
}

function formatReviewValue(value) {
  if (typeof value === "string" && value && !["true", "false"].includes(value)) return value;
  return toYesNoNa(value);
}

function principleKey(index) {
  return `principle${index + 1}`;
}

function getPrincipleValues(obj, formatter = toYesNoNa) {
  return Array.from({ length: PRINCIPLE_COUNT }, (_, i) =>
    formatter(obj?.[principleKey(i)])
  );
}

function getPerPrincipleText(obj, fallback = "") {
  if (obj && typeof obj === "object" && !Array.isArray(obj) && principleKey(0) in obj) {
    return getPrincipleValues(obj, (v) => (v ? String(v) : ""));
  }
  const text = fallback ? String(fallback) : "";
  return Array(PRINCIPLE_COUNT).fill(text);
}

function fillPrincipleRow(ws, row, values, startCol = COL_P_START, options = {}) {
  values.forEach((value, i) => {
    setCell(ws, row, startCol + i, value, {
      align: options.align || "center",
      fill: options.fill,
      bordered: false,
    });
  });
}

function fillFreqAreaValues(ws, row, values, options = {}) {
  values.forEach((value, i) => {
    setCell(ws, row, COL_FREQ_START + i, value, {
      align: options.align || "center",
      fill: options.fill ?? VALUE_FILL,
      bordered: false,
    });
  });
}

function addSectionTitle(ws, row, title) {
  const cell = ws.getCell(row, COL_QUESTION);
  cell.value = title;
  cell.font = TITLE_FONT;
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applySectionTitleStyle(ws, row);
  ws.getRow(row).height = 30;
}

function addTableHeaderRow(ws, row) {
  setCell(ws, row, COL_QUESTION, LABELS.disclosureQuestions, {
    bold: true,
    align: "center",
    bordered: false,
  });
  for (let i = 0; i < PRINCIPLE_COUNT; i += 1) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
    setCell(ws, row, COL_FREQ_START + i, `P${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
  }
  applyTableHeaderStyle(ws, row);
  ws.getRow(row).height = 27;
}

function addSubSectionBand(ws, row, title, { governance = false } = {}) {
  const cell = ws.getCell(row, COL_QUESTION);
  cell.value = title;
  cell.font = SUBTITLE_FONT;
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  if (governance) {
    applyGovernanceBandStyle(ws, row);
  } else {
    applySubBandStyle(ws, row);
  }
  ws.mergeCells(row, COL_QUESTION, row, COL_P_END);
  ws.getRow(row).height = 24;
}

function addQuestionPrincipleRow(ws, row, label, values, options = {}) {
  setCell(ws, row, COL_QUESTION, label, {
    align: "left",
    vertical: "top",
    bordered: false,
  });
  fillPrincipleRow(ws, row, values, COL_P_START, {
    align: options.align || "center",
  });
  if (options.style === "text") {
    applyTextPrincipleStyle(ws, row);
  } else {
    applyYesNoPrincipleStyle(ws, row, { labelBottom: options.labelBottom });
  }
  setAutoRowHeight(ws, row, [{ text: label, width: 80 }]);
  return row + 1;
}

function addPerPrincipleTextRow(ws, row, label, textOrObj) {
  return addQuestionPrincipleRow(
    ws,
    row,
    label,
    getPerPrincipleText(textOrObj, typeof textOrObj === "string" ? textOrObj : ""),
    { align: "left", style: "text" }
  );
}

function addGovernanceQuestionRow(ws, row, label, answer, options = {}) {
  setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top", bordered: false });
  setCell(ws, row, COL_GOV_ANSWER, answer || "", {
    align: "left",
    vertical: "top",
    bordered: false,
  });
  applyGovernanceRowStyle(ws, row, options);
  ws.mergeCells(row, COL_QUESTION, row, COL_GOV_LABEL_END);
  if (options.looseTop) {
    const labelCell = ws.getCell(row, COL_QUESTION);
    labelCell.border = { top: THIN, left: MEDIUM, right: THIN };
  }
  setAutoRowHeight(ws, row, [{ text: label, width: 70 }, { text: answer || "", width: 13 }]);
  ws.getRow(row).height = 30;
  return row + 1;
}

function addReviewSection(ws, row, gl) {
  const q10Label = LABELS.q10;
  setCell(ws, row, COL_QUESTION, q10Label, { align: "left", vertical: "top", bordered: false });
  applyReviewTitleStyle(ws, row);
  ws.mergeCells(row, COL_QUESTION, row, COL_P_END);
  setAutoRowHeight(ws, row, [{ text: q10Label, width: 80 }]);
  row += 1;

  const reviewHeader = LABELS.reviewHeader;
  const freqHeader = LABELS.freqHeader;
  const headerRow = row;
  const pHeaderRow = row + 1;

  setCell(ws, row, COL_QUESTION, LABELS.subjectForReview, {
    bold: true,
    align: "left",
    bordered: false,
  });
  setCell(ws, row, COL_P_START, reviewHeader, {
    bold: true,
    align: "center",
    vertical: "top",
    bordered: false,
  });
  setCell(ws, row, COL_FREQ_START, freqHeader, {
    bold: true,
    align: "center",
    vertical: "top",
    bordered: false,
  });
  applyReviewHeaderStyle(ws, headerRow, pHeaderRow);
  ws.mergeCells(headerRow, COL_P_START, headerRow, COL_P_END);
  ws.mergeCells(headerRow, COL_FREQ_START, headerRow, COL_FREQ_END);
  ws.getRow(headerRow).height = 27.75;
  row += 1;

  for (let i = 0; i < PRINCIPLE_COUNT; i += 1) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
    setCell(ws, row, COL_FREQ_START + i, `P${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
  }
  ws.mergeCells(headerRow, COL_QUESTION, pHeaderRow, COL_QUESTION);
  row += 1;

  const reviewSubjects = [
    {
      label: LABELS.reviewPerformance,
      reviewObj: gl?.performanceReviewDirector,
      freqObj: gl?.frequencyReviewDirector,
      descObj: gl?.descriptionOfOtherCommitteeForPerformance || gl?.performanceReviewDirectorDescription,
      descLabel: LABELS.reviewPerformanceDesc,
      descStyle: "split",
    },
    {
      label: LABELS.reviewCompliance,
      reviewObj: gl?.performanceCompliance,
      freqObj: gl?.frequencyCompliance,
      descObj: gl?.descriptionOfOtherCommitteeForCompliance || gl?.performanceComplianceDescription,
      descLabel: LABELS.reviewComplianceDesc,
      descStyle: "grey",
    },
  ];

  reviewSubjects.forEach(({ label, reviewObj, freqObj, descObj, descLabel, descStyle }) => {
    setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top", bordered: false });
    fillPrincipleRow(ws, row, getPrincipleValues(reviewObj, formatReviewValue));
    fillFreqAreaValues(ws, row, getPrincipleValues(freqObj, (v) => (v ? String(v) : "")));
    applyReviewDataStyle(ws, row, { labelBottom: descStyle === "grey" ? undefined : THIN });
    setAutoRowHeight(ws, row, [{ text: label, width: 80 }]);
    row += 1;

    setCell(ws, row, COL_QUESTION, descLabel, {
      align: "left",
      vertical: "top",
      bordered: false,
    });
    fillPrincipleRow(ws, row, getPerPrincipleText(descObj, ""), COL_P_START, { align: "left" });
    if (descStyle === "grey") {
      applyReviewDescGreyStyle(ws, row);
    } else {
      applyReviewDescStyle(ws, row);
    }
    ws.getRow(row).height = 30;
    row += 1;
  });

  return row;
}

function addIndependentAssessmentSection(ws, row, gl) {
  const q11Label = LABELS.q11;
  const headerRow = row;
  const dataRow = row + 1;

  setCell(ws, row, COL_QUESTION, q11Label, { align: "left", vertical: "top", bordered: false });

  for (let i = 0; i < PRINCIPLE_COUNT; i += 1) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
  }
  applyQ11HeaderStyle(ws, headerRow, dataRow);
  ws.mergeCells(row, COL_QUESTION, row + 1, COL_QUESTION);
  ws.getRow(row).height = 25.5;
  row += 1;

  setCell(ws, row, COL_QUESTION, q11Label, { align: "left", vertical: "top", bordered: false });
  fillPrincipleRow(ws, row, getPrincipleValues(gl?.extenalAgencyPolicyEvaluation));
  applyQ11DataStyle(ws, row);
  ws.getRow(row).height = 22.5;
  row += 1;

  setCell(ws, row, COL_QUESTION, LABELS.q11Agency, {
    align: "left",
    vertical: "top",
    bordered: false,
  });
  fillPrincipleRow(
    ws,
    row,
    getPrincipleValues(gl?.nameOfAgencyEvaluating, (v) => (v ? String(v) : "")),
    COL_P_START,
    { align: "left" }
  );
  applyQ11AgencyStyle(ws, row);
  ws.getRow(row).height = 32.25;
  return row + 1;
}

function addReasonsTable(ws, row, gl) {
  const q12Label = LABELS.q12;
  setCell(ws, row, COL_QUESTION, q12Label, { align: "left", vertical: "top", bordered: false });
  applyQ12TitleStyle(ws, row);
  ws.mergeCells(row, COL_QUESTION, row, COL_P_END);
  row += 1;

  setCell(ws, row, COL_QUESTION, LABELS.questionsHeader, {
    bold: true,
    align: "center",
    bordered: false,
  });
  for (let i = 0; i < PRINCIPLE_COUNT; i += 1) {
    setCell(ws, row, COL_P_START + i, `P ${i + 1}`, {
      bold: true,
      align: "center",
      bordered: false,
    });
  }
  applyQ12HeaderStyle(ws, row);
  row += 1;

  const reasonRows = [
    {
      label: LABELS.reasonNonMaterial,
      obj: gl?.nonMaterial,
    },
    {
      label: LABELS.reasonNotReady,
      obj: gl?.notReady,
    },
    {
      label: LABELS.reasonResources,
      obj: gl?.resourceConstraints,
    },
    {
      label: LABELS.reasonPlannedNextYear,
      obj: gl?.plannedNextYear,
    },
    {
      label: LABELS.reasonOther,
      obj: gl?.reasonRemark,
      isText: true,
    },
  ];

  reasonRows.forEach(({ label, obj, isText }) => {
    setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top", bordered: false });
    fillPrincipleRow(
      ws,
      row,
      getPrincipleValues(obj, isText ? (v) => (v ? String(v) : "-") : toYesNoNa)
    );
    applyReasonRowStyle(ws, row);
    setAutoRowHeight(ws, row, [{ text: label, width: 80 }]);
    ws.getRow(row).height = 29;
    row += 1;
  });

  setCell(ws, row, COL_QUESTION, LABELS.notes, { align: "left", bordered: false });
  applyNotesRowStyle(ws, row);
  ws.getRow(row).height = 30;
  return row + 1;
}

function setupMgtSheet(ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.1796875,
    D: 80.453125,
    E: 13.453125,
    F: 13.453125,
    G: 13.453125,
    H: 13.453125,
    I: 13.453125,
    J: 13.453125,
    K: 13.453125,
    L: 13.453125,
    M: 13.453125,
    N: 13.453125,
    O: 13.453125,
    P: 13.453125,
    Q: 13.453125,
    R: 13.453125,
    S: 13.453125,
    T: 13.453125,
    U: 13.453125,
    V: 13.453125,
    W: 9.1796875,
    X: 9.1796875,
    Y: 9.1796875,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ws.getColumn("A").hidden = true;
  ws.getColumn("B").hidden = true;
  // Visible through Y; hide Z → XFD.
  hideColumnsAfter(ws, 25);
  hideRowsThrough(ws, 5);

  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 3,
      ySplit: 7,
      topLeftCell: "D22",
      zoomScale: 70,
    },
  ];
}

export function buildMgtAndProcessDisclosureSheet(workbook, sectionB) {
  const ws = workbook.addWorksheet("MGT and process disclosure");
  setupMgtSheet(ws);

  const pm = sectionB?.policyAndManagement || {};
  const gl = sectionB?.governanceLeadership || {};

  let row = SECTION_ROW;
  addSectionTitle(ws, row, LABELS.sectionTitle);
  row += 1;

  addTableHeaderRow(ws, row);
  row += 1;

  addSubSectionBand(ws, row, LABELS.policyBand);
  row += 1;

  row = addQuestionPrincipleRow(
    ws,
    row,
    LABELS.q1a,
    getPrincipleValues(pm?.policyCoveringPrinciples)
  );
  ws.getRow(row - 1).height = 30;

  row = addQuestionPrincipleRow(
    ws,
    row,
    LABELS.q1b,
    getPrincipleValues(pm?.boardApprovedPolicies)
  );
  ws.getRow(row - 1).height = 30;

  setCell(ws, row, COL_QUESTION, LABELS.q1c, {
    align: "left",
    vertical: "top",
    bordered: false,
  });
  getPrincipleValues(pm?.webLinksForPolicies, (v) => v || "-").forEach((value, i) => {
    const col = COL_P_START + i;
    const url = pm?.webLinksForPolicies?.[principleKey(i)];
    if (url) {
      setCell(ws, row, col, url, { align: "left", link: url, bordered: false });
    } else {
      setCell(ws, row, col, "-", { align: "center", bordered: false });
    }
  });
  applyWebLinkRowStyle(ws, row);
  ws.getRow(row).height = 30;
  row += 1;

  const translated = toYesNoNa(pm?.policiesTranslatedToProcedures);
  row = addQuestionPrincipleRow(
    ws,
    row,
    LABELS.q2,
    Array(PRINCIPLE_COUNT).fill(translated)
  );
  ws.getRow(row - 1).height = 30;

  row = addQuestionPrincipleRow(
    ws,
    row,
    LABELS.q3,
    getPrincipleValues(pm?.policiesExtendToValueChainPartners),
    { labelBottom: undefined }
  );
  ws.getRow(row - 1).height = 30;

  row = addPerPrincipleTextRow(ws, row, LABELS.q4, pm?.codesCertificationsLabelsStandards);
  ws.getRow(row - 1).height = 43.5;

  row = addPerPrincipleTextRow(ws, row, LABELS.q5, pm?.commitmentsGoalsTargets);
  ws.getRow(row - 1).height = 30;

  row = addPerPrincipleTextRow(ws, row, LABELS.q6, pm?.performanceAgainstGoals);
  ws.getRow(row - 1).height = 29;

  addSubSectionBand(ws, row, LABELS.governanceBand, { governance: true });
  row += 1;

  row = addGovernanceQuestionRow(ws, row, LABELS.q7, gl?.directorStatementESG, { looseTop: true });

  row = addGovernanceQuestionRow(ws, row, LABELS.q8, gl?.oversightBusinessResponsibility);

  const committeeYesNo = gl?.independentAssessment?.status ? "Yes" : "No";
  row = addGovernanceQuestionRow(ws, row, LABELS.q9, committeeYesNo, { greyAnswerSpacer: true });

  row = addGovernanceQuestionRow(ws, row, LABELS.q9Details, gl?.independentAssessment?.remark || "");

  row = addReviewSection(ws, row, gl);
  row = addIndependentAssessmentSection(ws, row, gl);
  addReasonsTable(ws, row, gl);

  hideRowsThrough(ws, 5);
  return ws;
}
